import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import Button from '@atlaskit/button/custom-theme-button';
import RecentIcon from '@atlaskit/icon/core/clock';
import WorldIcon from '@atlaskit/icon/core/globe';

import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { EDITOR2_SHORTCUTS } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

import { WideTooltip } from './PublishButtonWithMoreOptions.tsx';
import { preloadPublishModal } from './preloadPublishModal.ts';

const i18n = defineMessages({
	schedulePublishButtonTooltip: {
		id: 'publish-modal.publish-modal-schedule-publish-button-tooltip',
		description:
			'Tooltip for scheduled button to inform user that they can change restrictions and location of the page while publishing',
		defaultMessage: 'Adjust publish schedule and more',
	},
	scheduleUpdateButtonTooltip: {
		id: 'publish-modal.publish-modal-schedule-update-button-tooltip',
		description:
			'Tooltip for scheduled button to inform user that they can change restrictions and location of the page while updating',
		defaultMessage: 'Adjust update schedule and more',
	},
	scheduledIcon: {
		id: 'publish-modal.schedule-publish-icon',
		description:
			'The text is shown as the accessible label of the scheduled publish icon button in the publish modal footer, indicating the action will publish the page at a scheduled time.',
		defaultMessage: 'Publish is scheduled for a future time',
	},
	scheduledPublishButton: {
		id: 'publish-modal.scheduled-publish-button',
		description:
			'Button to bring up publish modal dialog for user to edit settings for a page that is scheduled to be published',
		defaultMessage: 'Scheduled',
	},
	schedulePublicContentLabel: {
		id: 'publish-modal.public-schedule-publish-icon',
		description:
			'The text is shown as the accessible label of the scheduled publish icon button in the publish modal footer when the content is publicly/externally shared, indicating it will be published at a scheduled time.',
		defaultMessage: 'Public content is scheduled to publish at a future time',
	},
});

export type Props = {
	saveDraft?: () => void;
	openModalWithAnalytics: (string: any) => () => void;
	savingInProgress: boolean;
	isUnpublishedDraft: boolean;
	shouldShowWorldIcon: boolean;
	publishButtonDisabled: boolean | undefined;
};

export const ScheduledButton = ({
	saveDraft,
	openModalWithAnalytics,
	savingInProgress,
	isUnpublishedDraft,
	shouldShowWorldIcon,
	publishButtonDisabled,
}: Props): React.JSX.Element => {
	const intl = useIntl();

	const publishShortcutStrings = useShortcutStrings({ shortcut: EDITOR2_SHORTCUTS.PUBLISH });
	const tooltipText = isUnpublishedDraft
		? intl.formatMessage(i18n.schedulePublishButtonTooltip)
		: intl.formatMessage(i18n.scheduleUpdateButtonTooltip);

	return (
		<Tooltip component={WideTooltip} content={tooltipText} shortcut={publishShortcutStrings}>
			<Button
				appearance="primary"
				onMouseDown={() => preloadPublishModal(saveDraft)}
				onMouseOver={() => preloadPublishModal(saveDraft)}
				onFocus={() => preloadPublishModal(saveDraft)}
				onClick={openModalWithAnalytics('ScheduledButton')}
				// This test ID is used in tests and in experience tracking.  Be sure to update all occurences before changing it.
				testId="scheduled-button"
				iconBefore={
					shouldShowWorldIcon ? (
						<WorldIcon
							spacing="spacious"
							label={intl.formatMessage(i18n.schedulePublicContentLabel)}
						/>
					) : (
						<RecentIcon label={intl.formatMessage(i18n.scheduledIcon)} />
					)
				}
				isLoading={savingInProgress}
				isDisabled={publishButtonDisabled}
			>
				<FormattedMessage {...i18n.scheduledPublishButton} />
			</Button>
		</Tooltip>
	);
};
